import { useCallback, useMemo } from 'react';

import { useFormContext } from '@/components/react-hook-form';
import { getSourceValues } from '@/modules/estateWaterfall/EstateWaterfall.utils';
import { getNodes } from '@/utils/graphqlUtils';

import { useEstateWaterfallHypotheticalCharitableTrustModalContext } from '../EstateWaterfallHypotheticalCharitableTrustModal.context';
import { HypotheticalCharitableTrustFormShape } from '../EstateWaterfallHypotheticalCharitableTrustModal.types';
import {
  getProposalOptions,
  getSourceIndividualOptions,
  mapQueryDataToFormData,
  mapSourceEntityOptions,
} from '../EstateWaterfallHypotheticalCharitableTrustModal.utils';
import { EstateWaterfallHypotheticalCharitableTrustModalQuery } from '../graphql/EstateWaterfallHypotheticalCharitableTrustModal.generated';

export function useSyncDataToForm() {
  const {
    waterfallId,
    hypotheticalCharitableTrustId,
    initialSourceId,
    setProposalOptions,
    setSourceEntityOptions,
    setSourceIndividualOptions,
    setSourceValues,
    setFundingId,
    charitableTrustModalKind,
  } = useEstateWaterfallHypotheticalCharitableTrustModalContext();

  const { getValues, reset } =
    useFormContext<HypotheticalCharitableTrustFormShape>();

  const syncDataToForm = useCallback(
    (data: EstateWaterfallHypotheticalCharitableTrustModalQuery) => {
      const proposalOptions = getProposalOptions(
        data,
        charitableTrustModalKind,
        hypotheticalCharitableTrustId || undefined
      );
      setProposalOptions(proposalOptions);

      const sourceEntityOptions = mapSourceEntityOptions(data);
      const sourceIndividualOptions = getSourceIndividualOptions(data);

      setSourceEntityOptions(sourceEntityOptions);
      setSourceIndividualOptions(sourceIndividualOptions);

      // Find existing charitable trust to get its funding ID
      const waterfall = getNodes(data.estateWaterfalls)?.[0];
      const existingCharitableTrust = [
        ...getNodes(waterfall?.hypotheticalCrts),
        ...getNodes(waterfall?.hypotheticalClts),
      ]?.find((hc) => hc.id === hypotheticalCharitableTrustId);

      if (existingCharitableTrust?.funding?.id) {
        setFundingId(existingCharitableTrust.funding.id);
      }

      const sourceValues = getSourceValues(data);
      setSourceValues(sourceValues);

      // Let the context update before resetting the form. Read current values
      // here so a proposal created before the reset is not replaced by old data.
      setTimeout(() => {
        const formData = mapQueryDataToFormData({
          data,
          hypotheticalWaterfallId: waterfallId,
          initialSourceId,
          hypotheticalCharitableTrustId:
            hypotheticalCharitableTrustId || undefined,
          displayName: getValues('displayName'),
          sourceId: getValues('sourceId'),
          simulatedProposalId: getValues('simulatedProposalId'),
        });

        if (formData) {
          reset(formData);
        }
      });
    },
    [
      charitableTrustModalKind,
      hypotheticalCharitableTrustId,
      setProposalOptions,
      setSourceEntityOptions,
      setSourceIndividualOptions,
      setSourceValues,
      waterfallId,
      initialSourceId,
      getValues,
      setFundingId,
      reset,
    ]
  );

  return useMemo(
    () => ({
      syncDataToForm,
    }),
    [syncDataToForm]
  );
}
